/**
 * screens/ops/opsConsole.tsx
 * Softknack-internal Ops console (hire:admin). Surfaces the CC-002 internal
 * work items: pipeline ops metrics (WI-17), offline feedback analytics,
 * the skill curation queue (WI-4) with promote/attach/discard, and the batch
 * re-extraction trigger (WI-7). Not a tenant surface (design brief C.5).
 *
 * Presentation: a premium, motion-rich surface built on the app's shared
 * `Animated` idiom (see integrations.tsx) — a spring hero entrance, staggered
 * section + stat reveals, count-up metric numbers, hover-lift cards, an ambient
 * grayscale backdrop and a shimmer skeleton loader. All motion stays 100%
 * achromatic to honour the black-&-white design system.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, TextInput, Animated, Easing, Platform } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { ActionButton, Icon } from '../../components/ui';
import Popover from '../../components/popover';
import { showToast } from '../../components/toastComponent';
import {
  getOpsMetrics, getFeedbackAnalytics, getCurationQueue, getSkills,
  promoteCuration, attachCuration, discardCuration, reextract,
  getTitleQueue, promoteTitle, discardTitle,
  type OpsMetrics, type FeedbackAnalytics, type CurationEntry, type CanonicalSkill,
} from '../../services/hire/opsV2';

const NATIVE = Platform.OS !== 'web';

function titleCase(s: string): string {
  return s.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

type StatKind = 'int' | 'pct' | 'comma';
const formatStat = (n: number, kind: StatKind): string => {
  if (kind === 'pct') return `${Math.round(n * 100)}%`;
  if (kind === 'comma') return Math.round(n).toLocaleString();
  return String(Math.round(n));
};

type ThemeShape = ReturnType<typeof useTheme>['theme'];

export default function OpsConsoleScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { isMobile } = useBreakpoints();

  const [metrics, setMetrics] = useState<OpsMetrics | null>(null);
  const [feedback, setFeedback] = useState<FeedbackAnalytics | null>(null);
  const [queue, setQueue] = useState<CurationEntry[]>([]);
  const [titleQueue, setTitleQueue] = useState<CurationEntry[]>([]);
  const [skills, setSkills] = useState<CanonicalSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reexBusy, setReexBusy] = useState(false);
  const [targetVersion, setTargetVersion] = useState('v2');

  // Hero entrance driver + a slow ambient drift for the backdrop orbs.
  const hero = useRef(new Animated.Value(0)).current;
  const drift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.spring(hero, { toValue: 1, tension: 46, friction: 10, useNativeDriver: NATIVE }).start();
    const d = Animated.loop(
      Animated.sequence([
        Animated.timing(drift, { toValue: 1, duration: 9000, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE }),
        Animated.timing(drift, { toValue: 0, duration: 9000, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE }),
      ]),
    );
    d.start();
    return () => d.stop();
  }, [hero, drift]);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [m, f, q, s, tq] = await Promise.all([
        getOpsMetrics(), getFeedbackAnalytics(), getCurationQueue(), getSkills(), getTitleQueue(),
      ]);
      setMetrics(m); setFeedback(f); setQueue(q); setSkills(s); setTitleQueue(tq);
    } catch (e) {
      setError((e as { response?: { status?: number } })?.response?.status === 403
        ? 'Admin access required (hire:admin).'
        : 'Could not load ops data.');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const doPromote = async (row: CurationEntry) => {
    try { await promoteCuration(row.id, titleCase(row.rawText)); showToast('success', 'Promoted', `${row.rawText} → new canonical skill; backfilled.`); void load(true); }
    catch { showToast('error', 'Promote failed', row.rawText); }
  };
  const doAttach = async (row: CurationEntry, skill: CanonicalSkill) => {
    try { await attachCuration(row.id, skill.id); showToast('success', 'Attached', `${row.rawText} → ${skill.label}; backfilled.`); void load(true); }
    catch { showToast('error', 'Attach failed', row.rawText); }
  };
  const doDiscard = async (row: CurationEntry) => {
    try { await discardCuration(row.id); showToast('success', 'Discarded', row.rawText); void load(true); }
    catch { showToast('error', 'Discard failed', row.rawText); }
  };
  const doPromoteTitle = async (row: CurationEntry) => {
    try { await promoteTitle(row.id, titleCase(row.rawText), row.rawText); showToast('success', 'Title added', `${row.rawText} → title dictionary.`); void load(true); }
    catch { showToast('error', 'Promote failed', row.rawText); }
  };
  const doDiscardTitle = async (row: CurationEntry) => {
    try { await discardTitle(row.id); showToast('success', 'Discarded', row.rawText); void load(true); }
    catch { showToast('error', 'Discard failed', row.rawText); }
  };
  const doReextract = async () => {
    setReexBusy(true);
    try {
      const r = await reextract(targetVersion.trim() || 'v2');
      showToast('success', 'Re-extraction complete', `${r.reextracted} re-extracted · ${r.noop} no-op · ${r.skipped} skipped`);
      void load(true);
    } catch { showToast('error', 'Re-extraction failed', targetVersion); }
    finally { setReexBusy(false); }
  };

  if (loading) return <OpsSkeleton theme={theme} isMobile={isMobile} />;

  if (error) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 12 }}>
        <View style={{ width: 46, height: 46, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.dangerSoft, borderWidth: 1, borderColor: colors.dangerLine }}>
          <Icon name="alert-circle" size={20} color={colors.danger} />
        </View>
        <Text style={{ fontSize: fontSize.md, color: colors.ink }}>{error}</Text>
        <ActionButton label="Retry" variant="secondary" icon="refresh" onPress={() => void load()} />
      </View>
    );
  }

  const heroOpacity = hero;
  const heroTranslate = hero.interpolate({ inputRange: [0, 1], outputRange: [22, 0] });
  const orbA = drift.interpolate({ inputRange: [0, 1], outputRange: [-26, 26] });
  const orbB = drift.interpolate({ inputRange: [0, 1], outputRange: [22, -22] });

  // Metric decks — numbers stream in via count-up; each card reveals on a stagger.
  const pipeStats: StatItem[] = [
    { label: 'Match runs', value: metrics?.runs.total ?? 0, kind: 'int', sub: `${metrics?.runs.ok ?? 0} ok · ${metrics?.runs.fallback ?? 0} fallback · ${metrics?.runs.skipped ?? 0} skipped` },
    { label: 'Stage-4 fallback', value: metrics?.stage4FallbackRate ?? 0, kind: 'pct', sub: 'silent → Stage-3 order' },
    { label: 'Parse success', value: metrics?.parseSuccessRate ?? 0, kind: 'pct', sub: `${metrics?.parsedProfiles ?? 0} profiles · HIGH/MED` },
    { label: 'Rerank tokens', value: metrics?.tokenUsage.total ?? 0, kind: 'comma', sub: `${metrics?.tokenUsage.input ?? 0} in · ${metrics?.tokenUsage.output ?? 0} out` },
    { label: 'Stage-2 p95', value: metrics?.stage2P95Ms ?? 0, kind: 'comma', sub: `${metrics?.activePoolSize ?? 0} active pool${metrics?.annTriggerHit ? ' · ANN re-eval ⚠' : ' · no ANN'}` },
  ];
  const feedStats: StatItem[] = [
    { label: 'Verdicts', value: feedback?.total ?? 0, kind: 'int' },
    { label: 'Good match', value: feedback?.goodMatch ?? 0, kind: 'int' },
    { label: 'False positive', value: feedback?.falsePositive ?? 0, kind: 'int' },
    { label: 'False-positive rate', value: feedback?.falsePositiveRate ?? 0, kind: 'pct', sub: 'analytics-only · never re-trains' },
  ];

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 80 }}>
      {/* Ambient grayscale backdrop — two softly drifting radial washes. */}
      <Animated.View pointerEvents="none" style={[orbStyle(colors, { top: '4%', left: '6%', size: 300 }), { opacity: 0.55, transform: [{ translateX: orbA }, { translateY: orbB }] }]} />
      <Animated.View pointerEvents="none" style={[orbStyle(colors, { bottom: '2%', right: '4%', size: 360 }), { opacity: 0.45, transform: [{ translateX: orbB }, { translateY: orbA }] }]} />

      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 20 }}>
        {/* Hero */}
        <Animated.View style={{ opacity: heroOpacity, transform: [{ translateY: heroTranslate }] }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <View style={{ gap: 6, flex: 1, minWidth: 240 }}>
              <Text style={{ fontSize: isMobile ? 24 : 30, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.8 }}>Ops console</Text>
            </View>
            <View style={{ paddingTop: 4 }}>
              <ActionButton label="Refresh" variant="secondary" icon="refresh" loading={refreshing} onPress={() => void load(true)} />
            </View>
          </View>
        </Animated.View>

        {/* Pipeline metrics (WI-17) */}
        <Reveal delay={90}>
          <SectionCard title="Pipeline metrics" theme={theme}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
              {pipeStats.map((s, i) => <StatCard key={s.label} item={s} index={i} theme={theme} />)}
            </View>
          </SectionCard>
        </Reveal>

        {/* Feedback analytics (ground-truth loop) */}
        <Reveal delay={170}>
          <SectionCard title="Feedback analytics" theme={theme}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
              {feedStats.map((s, i) => <StatCard key={s.label} item={s} index={i} theme={theme} />)}
            </View>
          </SectionCard>
        </Reveal>

        {/* Skill curation queue (WI-4) */}
        <Reveal delay={250}>
          <SectionCard
            title="Skill curation queue"
            badge={`${queue.length} pending`}
            theme={theme}
            flush
          >
            {queue.length === 0 ? (
              <View style={{ alignItems: 'center', gap: 8, paddingVertical: 34 }}>
                <View style={{ width: 40, height: 40, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.successSoft, borderWidth: 1, borderColor: colors.successLine }}>
                  <Icon name="check" size={18} color={colors.success} />
                </View>
                <Text style={{ fontSize: fontSize.sm, color: colors.ink4 }}>No unmapped skills awaiting curation.</Text>
              </View>
            ) : (
              <View>
                {queue.map((row, i) => (
                  <QueueRow
                    key={row.id}
                    row={row}
                    index={i}
                    first={i === 0}
                    skills={skills}
                    theme={theme}
                    onPromote={() => void doPromote(row)}
                    onAttach={(s) => void doAttach(row, s)}
                    onDiscard={() => void doDiscard(row)}
                  />
                ))}
              </View>
            )}
          </SectionCard>
        </Reveal>

        {/* Title curation queue (WI-4 titles) */}
        <Reveal delay={300}>
          <SectionCard title="Title curation queue" badge={`${titleQueue.length} pending`} theme={theme} flush>
            {titleQueue.length === 0 ? (
              <View style={{ alignItems: 'center', gap: 8, paddingVertical: 34 }}>
                <View style={{ width: 40, height: 40, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.successSoft, borderWidth: 1, borderColor: colors.successLine }}>
                  <Icon name="check" size={18} color={colors.success} />
                </View>
                <Text style={{ fontSize: fontSize.sm, color: colors.ink4 }}>No uncurated titles.</Text>
              </View>
            ) : (
              <View>
                {titleQueue.map((row, i) => (
                  <View
                    key={row.id}
                    style={{
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'stretch' : 'center',
                      justifyContent: 'space-between',
                      gap: isMobile ? 10 : 12,
                      paddingVertical: isMobile ? 14 : 11,
                      paddingHorizontal: 14,
                      borderTopWidth: i === 0 ? 0 : 1,
                      borderTopColor: colors.line,
                    }}
                  >
                    <View style={{ flex: isMobile ? undefined : 1, minWidth: 0 }}>
                      <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink }}>{row.rawText}</Text>
                      <Text style={{ fontSize: 10, color: colors.ink4, marginTop: 1 }}>seen {row.occurrenceCount}×</Text>
                    </View>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <View style={isMobile ? { flex: 1 } : undefined}>
                        <ActionButton label="Add to dictionary" variant="primary" size="sm" onPress={() => void doPromoteTitle(row)} />
                      </View>
                      <View style={isMobile ? { flex: 1 } : undefined}>
                        <ActionButton label="Discard" variant="secondary" size="sm" onPress={() => void doDiscardTitle(row)} />
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </SectionCard>
        </Reveal>

        {/* Batch re-extraction (WI-7) */}
        <Reveal delay={330}>
          <SectionCard title="Batch re-extraction" theme={theme}>
            <View style={{ gap: 12 }}>
              <Text style={{ fontSize: fontSize.sm, lineHeight: 19, color: colors.ink3 }}>
                Re-extract all active candidates below the target extractor version. Idempotent per (candidate, version) · resumable · audit-logged · skips erased.
              </Text>
              <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'flex-end', flexWrap: 'wrap' }}>
                <View style={{ gap: 5, width: isMobile ? '100%' : undefined }}>
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>Target version</Text>
                  <TextInput
                    value={targetVersion}
                    onChangeText={setTargetVersion}
                    placeholder="v2"
                    placeholderTextColor={colors.ink4}
                    style={[{ width: isMobile ? '100%' : 120, height: 40, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, borderRadius: 6, backgroundColor: colors.card, color: colors.ink, fontFamily: font.mono }, web({ outline: 'none', transition: 'border-color 140ms ease' })]}
                  />
                </View>
                <View style={isMobile ? { width: '100%' } : undefined}>
                  <ActionButton label="Run re-extraction" variant="primary" icon="refresh" loading={reexBusy} onPress={() => void doReextract()} />
                </View>
              </View>
            </View>
          </SectionCard>
        </Reveal>
      </View>
    </ScrollView>
  );
}

/* ------------------------------------------------------------------ helpers */

interface StatItem { label: string; value: number; kind: StatKind; sub?: string }

function orbStyle(colors: ThemeShape['colors'], p: { top?: string; bottom?: string; left?: string; right?: string; size: number }) {
  return {
    position: 'absolute' as const,
    top: p.top as any, bottom: p.bottom as any, left: p.left as any, right: p.right as any,
    width: p.size, height: p.size, borderRadius: 999,
    ...(Platform.OS === 'web'
      ? ({ backgroundImage: `radial-gradient(circle, ${colors.accentSoft} 0%, transparent 68%)`, filter: 'blur(10px)' } as any)
      : { backgroundColor: colors.accentSoft }),
  };
}

/** Fade + rise entrance on a per-item delay — the app's staggered-reveal idiom. */
function Reveal({ delay = 0, children, style }: { delay?: number; children: React.ReactNode; style?: object }) {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const t = Animated.timing(v, { toValue: 1, duration: 560, delay, easing: Easing.out(Easing.cubic), useNativeDriver: NATIVE });
    t.start();
    return () => t.stop();
  }, [v, delay]);
  return (
    <Animated.View style={[{ opacity: v, transform: [{ translateY: v.interpolate({ inputRange: [0, 1], outputRange: [16, 0] }) }] }, style]}>
      {children}
    </Animated.View>
  );
}

/** Panel with a header (title + optional pill badge) that lifts subtly on hover. */
function SectionCard({
  title, badge, flush, children, theme,
}: { title: string; badge?: string; flush?: boolean; children: React.ReactNode; theme: ThemeShape }) {
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <View
      {...hoverProps}
      style={[
        { backgroundColor: colors.card, borderColor: hovered ? colors.lineStrong : colors.line, borderWidth: 1, borderRadius: radii.lg, overflow: 'hidden' },
        web({
          transition: 'transform 220ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 220ms ease, border-color 220ms ease',
          transform: hovered ? 'translateY(-2px)' : 'translateY(0px)',
          boxShadow: hovered
            ? '0 10px 30px -12px rgba(16,24,40,0.18), 0 2px 6px -3px rgba(16,24,40,0.10)'
            : '0 1px 0 rgba(16,24,40,0.04)',
        }),
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: colors.line }}>
        <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{title}</Text>
        {badge != null && (
          <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine }}>
            <Text style={{ fontFamily: theme.font.mono, fontSize: 10, fontWeight: fontWeight.semibold, color: colors.ink2, letterSpacing: 0.3 }}>{badge}</Text>
          </View>
        )}
      </View>
      <View style={flush ? undefined : { padding: 16 }}>{children}</View>
    </View>
  );
}

/** A metric tile: staggered entrance, count-up number, hover lift + accent rail. */
function StatCard({ item, index, theme }: { item: StatItem; index: number; theme: ThemeShape }) {
  const { colors, fontSize, fontWeight, font } = theme;
  const { hovered, hoverProps } = useHover();

  // Entrance.
  const enter = useRef(new Animated.Value(0)).current;
  // Count-up: animate a driver 0→1 and read it via listener to format the label.
  const countDriver = useRef(new Animated.Value(0)).current;
  const [display, setDisplay] = useState(() => formatStat(0, item.kind));

  useEffect(() => {
    const t = Animated.timing(enter, { toValue: 1, duration: 480, delay: 120 + index * 80, easing: Easing.out(Easing.cubic), useNativeDriver: NATIVE });
    t.start();
    return () => t.stop();
  }, [enter, index]);

  useEffect(() => {
    countDriver.setValue(0);
    const id = countDriver.addListener(({ value }) => setDisplay(formatStat(value * item.value, item.kind)));
    const t = Animated.timing(countDriver, { toValue: 1, duration: 900, delay: 200 + index * 80, easing: Easing.out(Easing.cubic), useNativeDriver: false });
    t.start();
    return () => { countDriver.removeListener(id); t.stop(); };
  }, [countDriver, item.value, item.kind, index]);

  return (
    <Animated.View
      {...hoverProps}
      style={[
        {
          flex: 1, minWidth: 140, gap: 4, paddingVertical: 15, paddingHorizontal: 15,
          borderWidth: 1, borderColor: colors.line, borderRadius: 12, backgroundColor: colors.card,
          overflow: 'hidden',
          opacity: enter,
          transform: [{ translateY: enter.interpolate({ inputRange: [0, 1], outputRange: [14, 0] }) }, { scale: enter.interpolate({ inputRange: [0, 1], outputRange: [0.97, 1] }) }],
        },
        web({
          transition: 'transform 200ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 200ms ease, border-color 200ms ease',
          transform: hovered ? 'translateY(-3px)' : 'translateY(0px)',
          borderColor: hovered ? colors.lineStrong : colors.line,
          boxShadow: hovered ? '0 12px 28px -14px rgba(16,24,40,0.22)' : 'none',
        }) as object,
      ]}
    >
      {/* Left accent rail — brightens on hover. */}
      <View style={[{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, backgroundColor: hovered ? colors.accent : colors.line }, web({ transition: 'background-color 200ms ease' })]} />
      <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>{item.label}</Text>
      <Text style={{ fontFamily: font.mono, fontSize: 24, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.5 }}>{display}</Text>
      {item.sub ? <Text style={{ fontSize: 10, color: colors.ink4 }}>{item.sub}</Text> : null}
    </Animated.View>
  );
}

/** Curation row: staggered slide-in + hover tint, with promote/attach/discard. */
function QueueRow({
  row, index, first, skills, theme, onPromote, onAttach, onDiscard,
}: {
  row: CurationEntry; index: number; first: boolean; skills: CanonicalSkill[]; theme: ThemeShape;
  onPromote: () => void; onAttach: (s: CanonicalSkill) => void; onDiscard: () => void;
}) {
  const { colors, fontSize, fontWeight, font } = theme;
  const { hovered, hoverProps } = useHover();
  const enter = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const t = Animated.timing(enter, { toValue: 1, duration: 420, delay: 260 + index * 55, easing: Easing.out(Easing.cubic), useNativeDriver: NATIVE });
    t.start();
    return () => t.stop();
  }, [enter, index]);

  return (
    <Animated.View
      {...hoverProps}
      style={[
        {
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          paddingVertical: 11, paddingHorizontal: 14,
          borderTopWidth: first ? 0 : 1, borderTopColor: colors.line,
          backgroundColor: hovered ? colors.paper2 : colors.transparent,
          opacity: enter,
          transform: [{ translateX: enter.interpolate({ inputRange: [0, 1], outputRange: [-10, 0] }) }],
        },
        web({ transition: 'background-color 160ms ease' }) as object,
      ]}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink }}>{row.rawText}</Text>
        <Text style={{ fontSize: 10, color: colors.ink4 }}>seen {row.occurrenceCount}×</Text>
      </View>
      <View style={{ flexDirection: 'row', gap: 6 }}>
        <ActionButton label="Promote" variant="primary" size="sm" onPress={onPromote} />
        <Popover
          align="right"
          width={240}
          trigger={<ActionButton label="Attach" variant="secondary" size="sm" onPress={() => {}} />}
        >
          {(close) => (
            <ScrollView style={{ maxHeight: 280 }}>
              {skills.map((s) => (
                <Text
                  key={s.id}
                  onPress={() => { close(); onAttach(s); }}
                  style={[{ paddingVertical: 8, paddingHorizontal: 10, fontSize: fontSize.sm, color: colors.ink }, web({ cursor: 'pointer' })]}
                >
                  {s.label}
                </Text>
              ))}
            </ScrollView>
          )}
        </Popover>
        <ActionButton label="Discard" variant="secondary" size="sm" onPress={onDiscard} />
      </View>
    </Animated.View>
  );
}

/* -------------------------------------------------------------- skeleton */

/** Loading state: shimmering placeholders so the console reads as "premium,
 *  loading" rather than a bare spinner. */
function OpsSkeleton({ theme, isMobile }: { theme: ThemeShape; isMobile: boolean }) {
  const { colors, fontWeight, font } = theme;
  const shimmer = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const a = Animated.loop(Animated.timing(shimmer, { toValue: 1, duration: 1300, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE }));
    a.start();
    return () => a.stop();
  }, [shimmer]);

  const Block = ({ h, w, r = 8, style }: { h: number; w?: number | string; r?: number; style?: object }) => {
    const sweepX = shimmer.interpolate({ inputRange: [0, 1], outputRange: [-160, 260] });
    return (
      <View style={[{ height: h, width: (w as any) ?? '100%', borderRadius: r, backgroundColor: colors.paper2, overflow: 'hidden' }, style]}>
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute', top: 0, bottom: 0, width: 120, transform: [{ translateX: sweepX }],
            ...(Platform.OS === 'web'
              ? ({ backgroundImage: `linear-gradient(90deg, transparent, ${colors.paper3}, transparent)` } as any)
              : { backgroundColor: colors.paper3, opacity: 0.4 }),
          }}
        />
      </View>
    );
  };

  const Card = () => (
    <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 10, backgroundColor: colors.card, padding: 16, gap: 14 }}>
      <Block h={14} w={160} r={6} />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        {[0, 1, 2, 3].map((i) => (
          <View key={i} style={{ flex: 1, minWidth: 140, gap: 8, padding: 15, borderWidth: 1, borderColor: colors.line, borderRadius: 12 }}>
            <Block h={10} w={80} r={4} />
            <Block h={22} w={64} r={6} />
            <Block h={9} w={110} r={4} />
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 80 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 20 }}>
        <View style={{ gap: 8 }}>
          <Text style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: 3, fontWeight: fontWeight.semibold, color: colors.ink4 }}>SOFTKNACK · INTERNAL</Text>
          <Block h={30} w={200} r={8} />
          <Block h={12} w={280} r={5} />
        </View>
        <Card />
        <Card />
      </View>
    </ScrollView>
  );
}
